import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { API_ENDPOINTS, AUTH_CRED } from './httpConstants';
import { AuthClient } from './authClient';
import { getSubdomain } from 'utils/getSubdomain';
import { errorKeyMessages } from './mappings/errorsMapping';
import { showForbiddenAlert } from 'utils/forbiddenAlert';

const workEnv = process.env.REACT_APP_WORK_ENVIRONMENT || 'localhost';

const baseURL = workEnv === 'codespace' ? '' : 'https://aspi.runasp.net';
console.log(`Work environment: ${workEnv}`);
console.log(`Using API base URL: ${baseURL}`);
const Axios: AxiosInstance = axios.create({
  baseURL: baseURL ?? 'https://aspi.runasp.net',
  timeout: 150000,
  headers: {},
});

Axios.interceptors.request.use((config: any) => {
  const tokenStr = localStorage.getItem(AUTH_CRED);
  let token = '';
  let tenantIdFromStorage = '';

  if (tokenStr) {
    try {
      const parsed = JSON.parse(tokenStr) as {
        token?: string;
        refreshToken?: string;
        tenantId?: string;
      };
      token = parsed.token ?? '';
      tenantIdFromStorage = parsed.tenantId ?? '';
    } catch {
      token = '';
      tenantIdFromStorage = '';
    }
  }

  if (!tenantIdFromStorage) {
    tenantIdFromStorage = localStorage.getItem('tenantId') || '';
  }

  // --- Tenant ID Resolution ---
  // Priority 1: Get from localStorage (set during login)
  const tenantId = tenantIdFromStorage;

  // Priority 2: Fallback to subdomain if not in localStorage
  // if (!tenantId) {
  //   tenantId = getSubdomain(process.env.REACT_APP_WORK_ENVIRONMENT || 'localhost');
  // }

  config.headers = {
    ...(config.headers || {}),
    Authorization: `Bearer ${token}`,
    ...(tenantId && { 'X-Tenant-ID': tenantId }),
  };

  return config;
});

Axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== API_ENDPOINTS.AUTH.LOGIN
    ) {
      originalRequest._retry = true;
      const credentials = HttpClient.getAuthCredentials();
      if (credentials?.refreshToken) {
        try {
          console.log('refreshing...');
          const { data } = await AuthClient.refresh({
            refreshToken: credentials.refreshToken,
          });
          console.log('refreshed');
          const { accessToken, refreshToken } = data || {};
          HttpClient.setAuthCredentials(accessToken ?? '', refreshToken ?? '');

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return Axios(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem(AUTH_CRED);
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }
    if (error.response?.status === 403) {
      const apiMessage =
        (error.response?.data as { message?: string })?.message ??
        'You are not allowed to perform this action.';
      showForbiddenAlert(apiMessage);
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      localStorage.removeItem(AUTH_CRED);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

export class HttpClient {
  static async get<T = any>(
    url: string,
    config: { params?: Record<string, any> } = {},
  ): Promise<T> {
    const response = await Axios.get<T>(url, config);
    return response.data;
  }

  static async post<T = any>(url: string, data?: any, options?: AxiosRequestConfig): Promise<T> {
    const response = await Axios.post<T>(url, data, options);
    return response.data;
  }

  static async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await Axios.put<T>(url, data);
    return response.data;
  }

  static async patch<T = any>(url: string, data?: any): Promise<T> {
    const response = await Axios.patch<T>(url, data);
    return response.data;
  }

  static async delete<T = any>(url: string): Promise<T> {
    const response = await Axios.delete<T>(url);
    return response.data;
  }

  static getAuthCredentials(): {
    token: string;
    refreshToken: string;
    tenantId?: string;
  } | null {
    const tokenStr = localStorage.getItem(AUTH_CRED);
    if (tokenStr) {
      try {
        const parsed = JSON.parse(tokenStr) as {
          token?: string;
          refreshToken?: string;
          tenantId?: string;
        };
        return {
          token: parsed.token ?? '',
          refreshToken: parsed.refreshToken ?? '',
          tenantId: parsed.tenantId,
        };
      } catch {
        return null;
      }
    }
    return null;
  }

  static setAuthCredentials(token: string, refreshToken: string, tenantId?: string): void {
    const existing = localStorage.getItem(AUTH_CRED);
    let existingTenantId = '';

    if (existing) {
      try {
        const parsed = JSON.parse(existing) as { tenantId?: string };
        existingTenantId = parsed.tenantId ?? '';
      } catch {
        existingTenantId = '';
      }
    }

    localStorage.setItem(
      AUTH_CRED,
      JSON.stringify({
        token,
        refreshToken,
        tenantId: tenantId || existingTenantId,
      }),
    );
  }
}

export function getFormErrors(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const errorKey = error.response?.data?.message;

    if (typeof errorKey === 'string') {
      return errorKeyMessages[errorKey] ?? errorKey;
    }
  }
  return null;
}

export function getFieldErrors(error: unknown): any | null {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.errors ?? null;
  }
  return null;
}
